/**
 * location-graph.ts — Location connectivity graph for world navigation.
 *
 * Defines travel connections between the 13 game locations with
 * distance (travel time), danger level, and route descriptions.
 */

// ─── Types ──────────────────────────────────────────────────────

export interface LocationConnection {
  /** Destination location ID. */
  to: string;
  /** Travel distance in hours. */
  hours: number;
  /** Frostbite exposure per trip (0-20). */
  frostbiteExposure: number;
  /** Route description shown during travel. */
  description: string;
  /** Danger level affects random encounter chance. */
  danger: 'safe' | 'moderate' | 'dangerous' | 'deadly';
}

export interface LocationGraphEntry {
  id: string;
  connections: LocationConnection[];
}

// ─── Graph Data ─────────────────────────────────────────────────

const GRAPH: LocationGraphEntry[] = [
  {
    id: 'castle-greymarch',
    connections: [
      { to: 'gallowcrest', hours: 8, frostbiteExposure: 3, description: 'The main road winds through snow-laden hills toward the capital.', danger: 'moderate' },
      { to: 'millhaven-village', hours: 4, frostbiteExposure: 2, description: 'A well-worn path through the lowlands leads to the farming village.', danger: 'safe' },
      { to: 'frozen-forest', hours: 6, frostbiteExposure: 5, description: 'An ancient trail leads into the dark, frozen treeline.', danger: 'dangerous' },
    ],
  },
  {
    id: 'gallowcrest',
    connections: [
      { to: 'castle-greymarch', hours: 8, frostbiteExposure: 3, description: 'The capital road descends through snow-covered passes.', danger: 'moderate' },
      { to: 'the-archive', hours: 2, frostbiteExposure: 0, description: 'Stone steps lead down into the vaults beneath the old palace.', danger: 'safe' },
      { to: 'winterguard-outpost', hours: 6, frostbiteExposure: 4, description: 'The northern road is heavily patrolled by the Winterguard.', danger: 'moderate' },
      { to: 'war-camp', hours: 4, frostbiteExposure: 2, description: 'A military road leads to the resistance encampment.', danger: 'safe' },
    ],
  },
  {
    id: 'millhaven-village',
    connections: [
      { to: 'castle-greymarch', hours: 4, frostbiteExposure: 2, description: 'The path back to the castle follows the frozen creek.', danger: 'safe' },
      { to: 'wilderness', hours: 3, frostbiteExposure: 3, description: 'Game trails lead into the untamed wilds beyond the village.', danger: 'moderate' },
    ],
  },
  {
    id: 'frozen-forest',
    connections: [
      { to: 'castle-greymarch', hours: 6, frostbiteExposure: 5, description: 'You retrace your steps through the frozen wood.', danger: 'dangerous' },
      { to: 'wilderness', hours: 4, frostbiteExposure: 4, description: 'The forest thins into open wilderness.', danger: 'moderate' },
      { to: 'thornhearth', hours: 8, frostbiteExposure: 6, description: 'A barely-visible trail leads deeper, toward the mountain settlement.', danger: 'dangerous' },
    ],
  },
  {
    id: 'wilderness',
    connections: [
      { to: 'frozen-forest', hours: 4, frostbiteExposure: 4, description: 'You push into the dark treeline.', danger: 'moderate' },
      { to: 'millhaven-village', hours: 3, frostbiteExposure: 3, description: 'Smoke from village hearths guides you back.', danger: 'moderate' },
      { to: 'frozen-wastes', hours: 6, frostbiteExposure: 8, description: 'The land opens into endless white. Nothing but ice and wind.', danger: 'deadly' },
    ],
  },
  {
    id: 'winterguard-outpost',
    connections: [
      { to: 'gallowcrest', hours: 6, frostbiteExposure: 4, description: 'The southern road leads back toward the capital.', danger: 'moderate' },
      { to: 'frozen-wastes', hours: 4, frostbiteExposure: 6, description: 'Beyond the outpost walls, the frozen wastes begin.', danger: 'dangerous' },
    ],
  },
  {
    id: 'frozen-wastes',
    connections: [
      { to: 'winterguard-outpost', hours: 4, frostbiteExposure: 6, description: 'You trudge south, the outpost barely visible through the blizzard.', danger: 'dangerous' },
      { to: 'wilderness', hours: 6, frostbiteExposure: 8, description: 'The endless ice gives way to scrubland.', danger: 'deadly' },
      { to: 'blackfrost-keep', hours: 10, frostbiteExposure: 12, description: 'The dark fortress looms on the horizon. Each step closer chills your soul.', danger: 'deadly' },
      { to: 'ancient-temple-ruins', hours: 8, frostbiteExposure: 10, description: 'Ancient markers guide you through the ice toward the temple plateau.', danger: 'deadly' },
    ],
  },
  {
    id: 'blackfrost-keep',
    connections: [
      { to: 'frozen-wastes', hours: 10, frostbiteExposure: 12, description: 'You flee the dark fortress into the frozen wastes.', danger: 'deadly' },
      { to: 'undercroft', hours: 1, frostbiteExposure: 0, description: 'A hidden passage descends into the labyrinth beneath the keep.', danger: 'dangerous' },
    ],
  },
  {
    id: 'undercroft',
    connections: [
      { to: 'blackfrost-keep', hours: 1, frostbiteExposure: 0, description: 'You climb back up toward the surface.', danger: 'dangerous' },
    ],
  },
  {
    id: 'thornhearth',
    connections: [
      { to: 'frozen-forest', hours: 8, frostbiteExposure: 6, description: 'The mountain path descends back into the forest.', danger: 'dangerous' },
      { to: 'war-camp', hours: 6, frostbiteExposure: 4, description: 'A hidden route through the mountains leads to the resistance camp.', danger: 'moderate' },
    ],
  },
  {
    id: 'war-camp',
    connections: [
      { to: 'gallowcrest', hours: 4, frostbiteExposure: 2, description: 'The military road leads back to the capital.', danger: 'safe' },
      { to: 'thornhearth', hours: 6, frostbiteExposure: 4, description: 'A mountain passage connects to the northern settlement.', danger: 'moderate' },
    ],
  },
  {
    id: 'the-archive',
    connections: [
      { to: 'gallowcrest', hours: 2, frostbiteExposure: 0, description: 'You ascend the stone steps back to the city above.', danger: 'safe' },
    ],
  },
  {
    id: 'ancient-temple-ruins',
    connections: [
      { to: 'frozen-wastes', hours: 8, frostbiteExposure: 10, description: 'You descend from the temple plateau into the frozen wasteland.', danger: 'deadly' },
    ],
  },
];

// ─── Lookup ─────────────────────────────────────────────────────

const graphMap = new Map<string, LocationGraphEntry>(
  GRAPH.map(entry => [entry.id, entry]),
);

/** Get connections from a location. */
export function getConnections(locationId: string): LocationConnection[] {
  return graphMap.get(locationId)?.connections ?? [];
}

/** Get a specific connection between two locations, if it exists. */
export function getConnection(from: string, to: string): LocationConnection | undefined {
  return getConnections(from).find(c => c.to === to);
}

/** Get all location IDs in the graph. */
export function getGraphLocationIds(): string[] {
  return GRAPH.map(e => e.id);
}

/** Check if two locations are directly connected. */
export function areConnected(from: string, to: string): boolean {
  return getConnections(from).some(c => c.to === to);
}
